import React, { useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { Location } from "history";
import type { IStore } from "reducers";
import { formatMessage } from "lib/globalize-util";
import { redirectToProduct } from "actions/item";
import OrderInsightsPanelContent from "./order-insights-panel-content";
import ProductStyleInsights from "./order-insights-product-styles/order-insights-product-styles";
import CategoryInsights from "./order-insights-categories";
import ProductColorInsights from "./colors/order-insights-colors";
import type { IItemRecord } from "records/item/item";
import { InsightsActions } from "constants/analytics";
import { INSIGHTS } from "constants/integration/insights";

interface IOrderInsightsProductStylesProps {
    onToggleInsightsSidePanel: () => void;
    location: Location;
}

interface IDispatchProps {
    onClickOnProduct: (product: Pick<IItemRecord, "_id" | "name">) => void;
}

interface StateProps {
    productInsightsNu67199: boolean;
    categoryInsightsNu67199: boolean;
    colorInsightsNu67199: boolean;
    insights: IStore["insights"];
}

const OrderInsightsGallery: React.FC<IOrderInsightsProductStylesProps & IDispatchProps & StateProps> = ({
    onToggleInsightsSidePanel,
    onClickOnProduct,
    productInsightsNu67199,
    categoryInsightsNu67199,
    colorInsightsNu67199,
    insights,
}) => {
    const hasScrolledRef = useRef(false);

    useEffect(() => {
        const parent = document.querySelector("#pagePanel");

        const handleScroll = (event) => {
            if (parent && event.target === parent && !hasScrolledRef.current) {
                hasScrolledRef.current = true;
                global.analytics.track(InsightsActions.drawerScroll);
            }
        };

        if (parent) {
            parent.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (parent) {
                hasScrolledRef.current = false;
                parent.removeEventListener("scroll", handleScroll);
            }
        };
    }, []);

    const tabs = useMemo(() => {
        const components = [];

        if (productInsightsNu67199) {
            components.push({
                label: formatMessage("Products"),
                key: INSIGHTS.COMPONENTS.STYLE_INSIGHTS,
                component: <ProductStyleInsights onClickOnProduct={onClickOnProduct} />,
            });
        }

        if (categoryInsightsNu67199) {
            components.push({
                label: formatMessage("Categories"),
                key: INSIGHTS.COMPONENTS.CATEGORY_INSIGHTS,
                component: <CategoryInsights />,
            });
        }

        if (colorInsightsNu67199) {
            components.push({
                label: formatMessage("Colors"),
                key: INSIGHTS.COMPONENTS.COLOR_INSIGHTS,
                component: <ProductColorInsights />,
            });
        }
        return components;
    }, [productInsightsNu67199, categoryInsightsNu67199, colorInsightsNu67199, onClickOnProduct]);

    if (tabs.length === 0) {
        return null;
    }

    return (
        <OrderInsightsPanelContent
            onToggleInsightsSidePanel={onToggleInsightsSidePanel}
            tabs={tabs}
            insights={insights}
        />
    );
};

const mapStateToProps = (state: IStore) => {
    return {
        productInsightsNu67199: state.ldFlags.productInsightsNu67199,
        categoryInsightsNu67199: state.ldFlags.categoryInsightsNu67199,
        colorInsightsNu67199: state.ldFlags.colorInsightsNu67199,
        insights: state.insights,
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    onClickOnProduct: (product: Pick<IItemRecord, "_id" | "name">) => {
        const { location } = ownProps;
        dispatch(redirectToProduct(product._id, { ...location, query: { orderType: "order_trends" } }));
    },
});

export const OrderInsightsGalleryContainer = connect<StateProps, IDispatchProps, IOrderInsightsProductStylesProps>(
    mapStateToProps,
    mapDispatchToProps
)(OrderInsightsGallery);
export default OrderInsightsGalleryContainer;
